import React from 'react';
import {
  doc,
  updateDoc,
  addDoc,
  setDoc,
  collection,
  deleteDoc,
  onSnapshot,
  Unsubscribe,
  DocumentData,
} from 'firebase/firestore';
import { db } from '@/integrations/firebase/firestore';
import {
  FirebaseContext,
  type FirebaseContextProps,
} from '@/context/firebase-context';

interface Props {
  children: React.ReactNode;
}

export const FirebaseProvider: React.FC<Props> = ({ children }) => {
  const updateDocument = async (
    collectionName: string,
    docId: string,
    data: DocumentData
  ): Promise<void> => {
    try {
      const docRef = doc(db, collectionName, docId);
      await updateDoc(docRef, data);
    } catch (error) {
      console.error('Error updating document:', error);
      throw error;
    }
  };

  const addDocument = async (
    collectionName: string,
    data: DocumentData,
    docId?: string
  ): Promise<string> => {
    try {
      if (docId) {
        // Use custom ID
        const docRef = doc(db, collectionName, docId);
        await setDoc(docRef, data);
        return docId;
      } else {
        // Auto-generate ID
        const collectionRef = collection(db, collectionName);
        const docRef = await addDoc(collectionRef, data);
        return docRef.id;
      }
    } catch (error) {
      console.error('Error adding document:', error);
      throw error;
    }
  };

  const deleteDocument = async (
    collectionName: string,
    docId: string
  ): Promise<void> => {
    try {
      const docRef = doc(db, collectionName, docId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  };

  const subscribeToDocument = <T,>(
    collectionName: string,
    docId: string,
    callback: (data: T | null) => void,
    onError?: (error: Error) => void
  ): Unsubscribe => {
    const docRef = doc(db, collectionName, docId);

    return onSnapshot(
      docRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          callback({ id: docSnapshot.id, ...docSnapshot.data() } as T);
        } else {
          callback(null);
        }
      },
      (error) => {
        console.error('Error listening to document:', error);
        if (onError) onError(error);
      }
    );
  };

  const value: FirebaseContextProps = {
    updateDocument,
    addDocument,
    deleteDocument,
    subscribeToDocument,
  };

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
};
